import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ArrowLeft, MapPin, Building2, Calendar, Store,
  TrendingUp, Wrench, Users, Target, CheckCircle2, ShoppingBag,
  BarChart3, Lightbulb, ChevronRight, Shield, PieChart, Layers,
  Key, Briefcase
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import { trackRecord, calendlyLink } from '../../data/mock';
import { CaseStudySchema } from '../../components/StructuredData';

const GriggsShoppingCenterPage = () => {
  // Get Griggs property data from trackRecord
  const property = trackRecord.find(p => p.name.includes('Griggs')) || trackRecord[3];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Property details
  const propertyDetails = {
    name: 'Griggs Shopping Center',
    location: '4546-4564 Griggs Rd, Houston, TX',
    submarket: 'Inner Houston / Griggs Road Retail Corridor',
    county: 'Harris County',
    assetClass: 'Retail',
    type: 'Neighborhood Shopping Center',
    status: 'Active',
    image: property.image
  };

  // Market opportunity points
  const marketOpportunity = [
    { title: 'Neighborhood Demand Drivers', description: 'Located in an established residential area with consistent foot traffic and local service demand' },
    { title: 'Underserved Retail Corridor', description: 'Limited quality retail options in the immediate trade area presenting leasing opportunities' },
    { title: 'Stabilization Opportunity', description: 'Potential to improve occupancy and tenant quality through active asset management' },
    { title: 'Value-Add Potential', description: 'Clear path to enhance property performance through tenant mix optimization and capital improvements' }
  ];

  // Tenant mix optimization strategies
  const tenantMixStrategies = [
    { title: 'Anchor Tenant Stability', description: 'Focus on securing and retaining credit-worthy anchor tenants to drive foot traffic' },
    { title: 'Service-Based Tenants', description: 'Target essential service providers (medical, personal services, food) that draw consistent visits' },
    { title: 'Lease Restructuring', description: 'Negotiate improved lease terms including rent escalations and tenant improvement allowances' },
    { title: 'Tenant Quality Enhancement', description: 'Screen for financially stable tenants with proven business operations' }
  ];

  // Property improvements
  const propertyImprovements = [
    { title: 'Facade Improvements', description: 'Exterior renovations to enhance visual appeal and attract quality tenants' },
    { title: 'Parking & Lighting', description: 'Parking lot maintenance, restriping, and improved lighting for safety and accessibility' },
    { title: 'Signage & Visibility', description: 'Monument signage updates and improved storefront visibility' },
    { title: 'Common Areas', description: 'Sidewalk repairs, landscaping, and general property maintenance' }
  ];

  // Operational strategies
  const operationalStrategies = [
    { metric: 'Broker Partnerships', description: 'Active relationships with local retail brokers to maintain leasing pipeline' },
    { metric: 'Local Tenant Sourcing', description: 'Targeted outreach to neighborhood businesses seeking quality space' },
    { metric: 'Vacancy Reduction', description: 'Proactive marketing and competitive positioning to minimize downtime' },
    { metric: 'Tenant Retention', description: 'Responsive property management to encourage lease renewals' }
  ];

  // Portfolio diversification benefits
  const diversificationBenefits = [
    'Retail provides different cash flow characteristics than multifamily, enhancing portfolio stability',
    'Neighborhood retail centers offer defensive qualities during economic cycles',
    'Service-based tenants (medical, personal care) provide consistent demand regardless of e-commerce trends',
    'Geographic and asset class diversification reduces overall portfolio risk'
  ];

  // Other case studies for navigation
  const otherCaseStudies = trackRecord.filter(p => !p.name.includes('Griggs')).slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Griggs Shopping Center Case Study | Houston Retail Investment | ASAP Capital</title>
        <meta 
          name="description" 
          content="Retail real estate investment case study of Griggs Shopping Center in Houston, focused on tenant mix optimization and operational improvements." 
        />
        <meta name="keywords" content="Griggs Shopping Center, Houston retail investment, shopping center investment, retail real estate case study, neighborhood retail center, tenant mix optimization, commercial real estate Houston" />
        <link rel="canonical" href="/case-studies/griggs-shopping-center" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* ==================== HERO SECTION ==================== */}
          <section className="relative pt-32 pb-0 lg:pt-40 overflow-hidden">
            {/* Background elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
              {/* Breadcrumb */}
              <motion.div 
                className="flex items-center gap-2 text-sm mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Link to="/case-studies" className="text-white/50 hover:text-[#D4AF37] transition-colors">
                  Case Studies
                </Link>
                <ChevronRight className="h-4 w-4 text-white/30" />
                <span className="text-white/70">Griggs Shopping Center</span>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-10 items-end"
              >
                {/* Left - Content */}
                <div className="pb-10 lg:pb-16">
                  <motion.div 
                    className="flex items-center gap-3 mb-4"
                    variants={itemVariants}
                  >
                    <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-blue-500/20 text-blue-400 border-blue-500/30">
                      {propertyDetails.status}
                    </span>
                    <span className="px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs border border-white/10">
                      {propertyDetails.assetClass}
                    </span>
                  </motion.div>

                  <motion.h1 
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.2] mb-2"
                    variants={itemVariants}
                  >
                    Griggs Shopping Center Retail Investment Case Study
                  </motion.h1>

                  <motion.p 
                    className="text-lg text-[#D4AF37] mb-6"
                    variants={itemVariants}
                  >
                    Houston Neighborhood Retail Investment
                  </motion.p>

                  <motion.div 
                    className="flex flex-wrap gap-6 text-white/60 text-sm mb-8"
                    variants={itemVariants}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#D4AF37]" />
                      <span>Houston, Texas</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Store className="h-4 w-4 text-[#D4AF37]" />
                      <span>{propertyDetails.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-[#D4AF37]" />
                      <span>Active Investment</span>
                    </div>
                  </motion.div>

                  <motion.p 
                    className="text-white/50 text-sm italic border-l-2 border-[#D4AF37]/30 pl-4"
                    variants={itemVariants}
                  >
                    Past performance is not indicative of future results. Investment opportunities are offered only through a Private Placement Memorandum to investors.
                  </motion.p>
                </div>

                {/* Right - Image */}
                <motion.div 
                  className="relative"
                  variants={itemVariants}
                >
                  <div className="relative rounded-t-2xl lg:rounded-2xl overflow-hidden">
                    <img 
                      src={propertyDetails.image}
                      alt={propertyDetails.name}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROPERTY OVERVIEW ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-8" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                    <Store className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Property Overview
                  </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { label: 'Property Name', value: propertyDetails.name, subValue: propertyDetails.type, icon: Store },
                    { label: 'Location', value: 'Houston, TX', subValue: propertyDetails.submarket, icon: MapPin },
                    { label: 'Asset Type', value: 'Retail Center', subValue: 'Strip Center Format', icon: ShoppingBag },
                    { label: 'Investment Status', value: propertyDetails.status, subValue: 'Active Asset Management', icon: Briefcase }
                  ].map((item, index) => (
                    <motion.div
                      key={item.label}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                      variants={itemVariants}
                    >
                      <item.icon className="h-6 w-6 text-[#D4AF37] mb-3" />
                      <p className="text-white/40 text-xs mb-1">{item.label}</p>
                      <p className="text-white font-bold text-lg">{item.value}</p>
                      {item.subValue && <p className="text-white/50 text-sm">{item.subValue}</p>}
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 p-5 bg-white/5 rounded-xl border border-white/5"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-white/70 font-medium mb-1">Full Address</p>
                      <p className="text-white/50 text-sm">{propertyDetails.location}</p>
                      <p className="text-white/40 text-sm mt-1">{propertyDetails.county}, Inner Houston Neighborhood Retail Market</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== MARKET OPPORTUNITY AND ACQUISITION RATIONALE ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Target className="h-5 w-5 text-blue-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Market Opportunity and Acquisition Rationale
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Griggs Shopping Center represents a strategic retail acquisition in Houston's 
                  inner-city neighborhood retail market. The property was identified through our 
                  disciplined screening process, which targets quality assets in established 
                  commercial corridors with clear value creation potential.
                </motion.p>

                <div className="grid md:grid-cols-2 gap-6">
                  {marketOpportunity.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5 hover:border-blue-500/30 transition-all"
                      variants={itemVariants}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                        <CheckCircle2 className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                        <p className="text-white/50 text-sm">{item.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== TENANT MIX OPTIMIZATION STRATEGY ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                    <Key className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Tenant Mix Optimization Strategy
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Our tenant mix strategy focuses on creating a balanced, complementary roster 
                  of businesses that drive consistent foot traffic and enhance the overall 
                  shopping experience for the surrounding community.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {tenantMixStrategies.map((item, index) => (
                    <motion.div
                      key={item.title}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 hover:border-emerald-500/30 transition-all"
                      variants={itemVariants}
                      whileHover={{ y: -5 }}
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-3">
                        <span className="text-emerald-400 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-emerald-500/20"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-4">
                    <ShoppingBag className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">Target Tenant Categories</h4>
                      <div className="flex flex-wrap gap-2">
                        {['Medical Services', 'Personal Services', 'Food & Beverage', 'Professional Services', 'Convenience Retail', 'Financial Services'].map((category, index) => (
                          <span key={index} className="px-3 py-1 rounded-full bg-white/5 text-white/60 text-sm border border-white/10">
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== PROPERTY IMPROVEMENTS AND CAPITAL ENHANCEMENTS ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                    <Wrench className="h-5 w-5 text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Property Improvements and Capital Enhancements
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Strategic capital improvements are designed to enhance property appeal, 
                  attract quality tenants, and support long-term value creation for the asset.
                </motion.p>

                <div className="grid lg:grid-cols-2 gap-10">
                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Improvement Categories</h4>
                    <div className="space-y-4">
                      {propertyImprovements.map((item, index) => (
                        <div key={item.title} className="flex items-start gap-4 p-4 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5">
                          <span className="text-amber-400 font-bold text-sm mt-1">{String(index + 1).padStart(2, '0')}</span>
                          <div>
                            <p className="text-white font-medium">{item.title}</p>
                            <p className="text-white/50 text-sm">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants}>
                    <h4 className="text-white font-semibold mb-4">Enhancement Objectives</h4>
                    <div className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10">
                      <ul className="space-y-3">
                        {[
                          'Improve property curb appeal to attract higher-quality tenants',
                          'Enhance parking lot safety and accessibility',
                          'Upgrade signage for improved visibility and tenant exposure',
                          'Maintain common areas to institutional standards',
                          'Create inviting environment for neighborhood customers'
                        ].map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <span className="text-white/60 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ==================== OPERATIONAL MANAGEMENT AND LEASING STRATEGY ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0A1628] to-[#0d1a2d]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                    <Users className="h-5 w-5 text-purple-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Operational Management and Leasing Strategy
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Our operational approach emphasizes proactive leasing, responsive property 
                  management, and strategic tenant relationships to maximize property performance.
                </motion.p>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {operationalStrategies.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-5 border border-white/10 text-center"
                      variants={itemVariants}
                      whileHover={{ y: -5, borderColor: 'rgba(168, 85, 247, 0.3)' }}
                    >
                      <h4 className="text-white font-semibold mb-2">{item.metric}</h4>
                      <p className="text-white/50 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Disclaimer */}
                <motion.div 
                  className="bg-white/[0.02] border border-white/5 rounded-xl p-4"
                  variants={itemVariants}
                >
                  <p className="text-white/40 text-xs text-center">
                    <Shield className="h-4 w-4 inline-block mr-2 text-white/30" />
                    Asset management strategies shown are operational approaches and do not guarantee specific results. 
                    All investments involve risk. Please refer to offering documents for complete details.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== ROLE OF RETAIL ASSETS IN PORTFOLIO DIVERSIFICATION ==================== */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
              >
                <motion.div className="flex items-center gap-3 mb-6" variants={itemVariants}>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                    <PieChart className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-white">
                    Role of Retail Assets in Portfolio Diversification
                  </h2>
                </motion.div>

                <motion.p 
                  className="text-white/60 max-w-3xl mb-8"
                  variants={itemVariants}
                >
                  Neighborhood retail centers like Griggs Shopping Center play a strategic role 
                  in portfolio construction, providing diversification benefits that complement 
                  our multifamily and office holdings.
                </motion.p>

                <div className="grid lg:grid-cols-2 gap-6">
                  {diversificationBenefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-4 p-5 bg-gradient-to-r from-[#111D35] to-transparent rounded-xl border border-white/5"
                      variants={itemVariants}
                    >
                      <div className="w-8 h-8 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0 border border-cyan-500/20">
                        <span className="text-cyan-400 font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-white/60">{benefit}</p>
                    </motion.div>
                  ))}
                </div>

                <motion.div 
                  className="mt-8 bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-cyan-500/20"
                  variants={itemVariants}
                >
                  <div className="flex items-start gap-4">
                    <Layers className="h-6 w-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">Multi-Asset Class Portfolio Benefits</h4>
                      <p className="text-white/50 text-sm">
                        By including neighborhood retail alongside multifamily and office investments, 
                        ASAP Capital constructs portfolios designed to balance income stability with 
                        growth potential across different market cycles. Retail assets provide exposure 
                        to local consumer spending while reducing correlation with residential rental markets.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== STRATEGY FIT ==================== */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
                className="grid lg:grid-cols-2 gap-12 items-center"
              >
                <motion.div variants={itemVariants}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/20">
                      <TrendingUp className="h-5 w-5 text-[#D4AF37]" />
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold text-white">
                      How This Investment Aligns With Our Strategy
                    </h2>
                  </div>

                  <p className="text-white/60 mb-6">
                    Griggs Shopping Center exemplifies our commitment to building diversified 
                    real estate portfolios in the Greater Houston market. By actively managing 
                    neighborhood retail assets alongside our multifamily holdings, we seek to 
                    optimize risk-adjusted returns for our investors.
                  </p>

                  <p className="text-white/60 mb-6">
                    This investment demonstrates our ability to identify, acquire, and manage 
                    commercial retail assets through our vertically integrated platform, applying 
                    the same disciplined approach we bring to all asset classes.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Link to="/strategy">
                      <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold rounded-full">
                        View Investment Strategy
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/portfolio">
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full">
                        View Portfolio
                      </Button>
                    </Link>
                  </div>
                </motion.div>

                <motion.div 
                  className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-6 border border-white/10"
                  variants={itemVariants}
                >
                  <h4 className="text-white font-semibold mb-6">Our Retail Asset Approach</h4>
                  <div className="space-y-4">
                    {[
                      { title: 'Strategic Acquisition', description: 'Target quality retail in established neighborhood corridors' },
                      { title: 'Tenant Mix Management', description: 'Curate complementary tenants to drive consistent traffic' },
                      { title: 'Property Enhancement', description: 'Strategic improvements to maintain competitive positioning' },
                      { title: 'Portfolio Integration', description: 'Balance retail with multifamily for diversified returns' }
                    ].map((item, index) => (
                      <div key={item.title} className="flex items-start gap-4">
                        <span className="text-[#D4AF37] font-bold">{String(index + 1).padStart(2, '0')}</span>
                        <div>
                          <p className="text-white font-medium">{item.title}</p>
                          <p className="text-white/50 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ==================== EXPLORE MORE CTA ==================== */}
          <section className="py-20 lg:py-28 relative">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Explore Additional Case Studies and Opportunities
                </h2>
                <p className="text-white/50 max-w-2xl mx-auto">
                  Review additional examples of our value-add investment approach across Houston's 
                  multifamily and commercial real estate markets.
                </p>
              </motion.div>

              {/* Other Case Studies */}
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {otherCaseStudies.map((study) => (
                  <motion.div
                    key={study.id}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img 
                        src={study.image} 
                        alt={study.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[#D4AF37] text-sm mb-1">{study.type}</p>
                      <h4 className="text-white font-semibold mb-2 group-hover:text-[#D4AF37] transition-colors">
                        {study.name}
                      </h4>
                      <p className="text-white/50 text-sm">{study.location}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <Link to="/case-studies">
                  <Button className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0A1628] font-semibold px-8 py-3 h-12 rounded-full shadow-lg shadow-[#D4AF37]/20">
                    View All Case Studies
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/portfolio">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-3 h-12 rounded-full">
                    View Portfolio
                  </Button>
                </Link>
                <motion.a 
                  href={calendlyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-8 py-3 h-12 rounded-full">
                    Schedule Consultation
                    <Calendar className="ml-2 h-4 w-4" />
                  </Button>
                </motion.a>
              </motion.div>
            </div>
          </section>

          {/* ==================== BACK LINK ==================== */}
          <section className="py-8 relative border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <Link 
                to="/case-studies"
                className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Case Studies
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default GriggsShoppingCenterPage;
